import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { averageMedianCalculatorRoute } from '@/shared/utility-registry'

const UTILITY = averageMedianCalculatorRoute.analyticsKey
const NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/
const EXAMPLE_INPUT = '12 18 24\n31; 42\n15,5\tbad-token\n20.25'

interface ParsedNumbers {
  values: number[]
  ignored: string[]
}

interface SummaryResult {
  count: number
  sum: number
  mean: number
  median: number
  min: number
  max: number
  range: number
}

interface AnalysisState extends ParsedNumbers {
  summary: SummaryResult | null
  status: 'empty' | 'ready' | 'invalid-only'
}

interface ResultRow {
  label: string
  value: string
}

type CopyStatus = 'idle' | 'copied' | 'error'

function parseNumberToken(token: string): number | null {
  const normalized = token.includes(',') && !token.includes('.')
    ? token.replace(',', '.')
    : token

  if (!NUMBER_PATTERN.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function tokenizeChunk(rawChunk: string): string[] {
  const chunk = rawChunk.trim().replace(/^,+|,+$/g, '')
  if (chunk === '') return []
  if (!chunk.includes(',')) return [chunk]

  const commaCount = (chunk.match(/,/g) ?? []).length
  const isDecimalComma =
    commaCount === 1 && !chunk.includes('.') && /^[+-]?\d+,\d+$/.test(chunk)

  if (isDecimalComma) return [chunk]

  return chunk
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
}

function parseNumberList(input: string): ParsedNumbers {
  const values: number[] = []
  const ignored: string[] = []
  const chunks = input.split(/[\s;]+/).filter(Boolean)

  chunks.forEach((chunk) => {
    const tokens = tokenizeChunk(chunk)

    tokens.forEach((token) => {
      const parsed = parseNumberToken(token)

      if (parsed === null) {
        ignored.push(token)
        return
      }

      values.push(parsed)
    })
  })

  return { values, ignored }
}

function summarize(values: number[]): SummaryResult | null {
  if (values.length === 0) return null

  const sorted = [...values].sort((left, right) => left - right)
  const sum = values.reduce((total, value) => total + value, 0)
  const middle = Math.floor(sorted.length / 2)
  const median = sorted.length % 2 === 0
    ? (sorted[middle - 1]! + sorted[middle]!) / 2
    : sorted[middle]!
  const min = sorted[0]!
  const max = sorted[sorted.length - 1]!

  return {
    count: values.length,
    sum,
    mean: sum / values.length,
    median,
    min,
    max,
    range: max - min,
  }
}

function analyzeInput(input: string): AnalysisState {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { values: [], ignored: [], summary: null, status: 'empty' }
  }

  const parsed = parseNumberList(trimmed)
  const summary = summarize(parsed.values)

  return {
    ...parsed,
    summary,
    status: summary ? 'ready' : 'invalid-only',
  }
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 10,
  }).format(value)
}

function resultRows(summary: SummaryResult): ResultRow[] {
  return [
    { label: 'Количество', value: String(summary.count) },
    { label: 'Сумма', value: formatNumber(summary.sum) },
    { label: 'Среднее арифметическое', value: formatNumber(summary.mean) },
    { label: 'Медиана', value: formatNumber(summary.median) },
    { label: 'Минимум', value: formatNumber(summary.min) },
    { label: 'Максимум', value: formatNumber(summary.max) },
    { label: 'Размах', value: formatNumber(summary.range) },
  ]
}

function rowsToClipboard(rows: ResultRow[]): string {
  return rows.map((row) => `${row.label}\t${row.value}`).join('\n')
}

export function AverageMedianCalculatorPage() {
  const [input, setInput] = useState('')
  const [analysis, setAnalysis] = useState<AnalysisState>(() => analyzeInput(''))
  const [isParsing, setIsParsing] = useState(false)
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')

  useEffect(() => {
    const trimmed = input.trim()
    setCopyStatus('idle')

    if (trimmed === '') {
      setIsParsing(false)
      setAnalysis(analyzeInput(''))
      return undefined
    }

    setIsParsing(true)

    const delayMs = input.length > 3000 ? 220 : 120
    const timeout = window.setTimeout(() => {
      const startMs = performance.now()
      toolInvoke(UTILITY, { length: input.length })

      const nextAnalysis = analyzeInput(input)
      setAnalysis(nextAnalysis)
      setIsParsing(false)

      if (nextAnalysis.summary) {
        toolSuccess(UTILITY, Math.round(performance.now() - startMs), {
          count: nextAnalysis.summary.count,
          ignored_count: nextAnalysis.ignored.length,
        })
        return
      }

      if (nextAnalysis.ignored.length > 0) {
        toolError(UTILITY, 'no_valid_numbers', {
          ignored_count: nextAnalysis.ignored.length,
        })
      }
    }, delayMs)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [input])

  const rows = useMemo(
    () => (analysis.summary ? resultRows(analysis.summary) : []),
    [analysis.summary],
  )

  const handleInput = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value)
  }, [])

  const reset = useCallback(() => {
    setInput('')
    setAnalysis(analyzeInput(''))
    setIsParsing(false)
    setCopyStatus('idle')
  }, [])

  const fillExample = useCallback(() => {
    setInput(EXAMPLE_INPUT)
  }, [])

  const copyTable = useCallback(async () => {
    if (rows.length === 0 || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(rowsToClipboard(rows))
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [rows])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500"
        />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
          <div className="space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div>
              <label htmlFor="average-median-input" className="text-base font-semibold tracking-normal">
                Список чисел
              </label>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Вставьте числа через пробелы, строки, запятые, точки с запятой или табы.
              </p>
              <textarea
                id="average-median-input"
                value={input}
                onChange={handleInput}
                spellCheck={false}
                placeholder={'12 18 24\n31; 42\n15,5   20.25'}
                className={cn(
                  'mt-2 min-h-[120px] w-full resize-y rounded-md border bg-background px-3 py-3 font-mono text-sm leading-6 outline-none transition-colors sm:mt-3 sm:min-h-[230px]',
                  'focus:border-primary focus:ring-2 focus:ring-ring/20',
                )}
              />
            </div>

            <InvalidTokensPanel ignored={analysis.ignored} />

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={fillExample}>Пример</Button>
              {rows.length > 0 && (
                <Button variant="outline" onClick={copyTable}>
                  Скопировать таблицу
                </Button>
              )}
              {input.trim() !== '' && <Button variant="ghost" onClick={reset}>Сбросить</Button>}
            </div>

            <CopyStatusMessage status={copyStatus} />
          </div>

          <SummaryPanel analysis={analysis} isParsing={isParsing} rows={rows} />
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Авторасчёт" text="Итоги обновляются после вставки или изменения списка." />
        <InfoCard title="Десятичная запятая" text="15,5 читается как дробное число, когда запятая не выглядит разделителем списка." />
        <InfoCard title="Без сервера" text="Расчёт среднего, медианы и суммы выполняется локально в браузере." />
      </section>
    </div>
  )
}

function SummaryPanel({
  analysis,
  isParsing,
  rows,
}: {
  analysis: AnalysisState
  isParsing: boolean
  rows: ResultRow[]
}) {
  return (
    <div className="flex flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[420px]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-muted-foreground">Результаты</div>
        <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
          {isParsing ? 'Обновляем...' : `${analysis.values.length} чисел`}
        </span>
      </div>

      {analysis.summary ? (
        <div>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <MetricCard label="Среднее" value={formatNumber(analysis.summary.mean)} accent="primary" />
            <MetricCard label="Медиана" value={formatNumber(analysis.summary.median)} accent="amber" />
          </div>

          <div className="mt-5 overflow-x-auto rounded-md border bg-card shadow-sm">
            <table className="w-full min-w-[360px] border-collapse text-sm">
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-b last:border-b-0">
                    <th className="w-1/2 px-3 py-3 text-left font-medium text-muted-foreground">
                      {row.label}
                    </th>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-foreground">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState status={analysis.status} />
      )}
    </div>
  )
}

function MetricCard({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent: 'primary' | 'amber'
}) {
  return (
    <div className={cn(
      'rounded-md border bg-card p-4 shadow-sm',
      accent === 'primary' ? 'border-primary/35' : 'border-amber-400/45',
    )}
    >
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-2 break-words font-mono text-3xl font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  )
}

function EmptyState({ status }: { status: AnalysisState['status'] }) {
  const title = status === 'invalid-only'
    ? 'Не нашли чисел для расчёта'
    : 'Вставьте список чисел'
  const text = status === 'invalid-only'
    ? 'Проверьте токены ниже: калькулятор покажет, что было проигнорировано.'
    : 'Когда появится хотя бы одно число, таблица обновится автоматически.'

  return (
    <div className="rounded-md border bg-card p-4 shadow-sm">
      <div className="font-semibold">{title}</div>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
    </div>
  )
}

function InvalidTokensPanel({ ignored }: { ignored: string[] }) {
  if (ignored.length === 0) {
    return (
      <div className="rounded-md border bg-background/70 px-3 py-2 text-sm text-muted-foreground">
        Некорректные токены будут показаны здесь отдельно.
      </div>
    )
  }

  const visibleTokens = ignored.slice(0, 16)
  const hiddenCount = ignored.length - visibleTokens.length

  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
      <div className="font-medium">Проигнорировано: {ignored.length}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {visibleTokens.map((token, index) => (
          <span key={`${token}-${index}`} className="rounded border border-destructive/30 bg-background px-2 py-1 font-mono text-xs">
            {token}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="rounded border border-destructive/30 bg-background px-2 py-1 text-xs">
            +{hiddenCount}
          </span>
        )}
      </div>
    </div>
  )
}

function CopyStatusMessage({ status }: { status: CopyStatus }) {
  if (status === 'idle') return null

  return (
    <div className={cn(
      'rounded-md border px-3 py-2 text-sm',
      status === 'copied'
        ? 'border-primary/35 bg-primary/10 text-primary'
        : 'border-destructive/40 bg-destructive/10 text-destructive',
    )}
    >
      {status === 'copied'
        ? 'Таблица скопирована.'
        : 'Не удалось скопировать таблицу автоматически.'}
    </div>
  )
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="p-4">
        <h2 className="font-semibold tracking-normal">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}
